import { useCallback } from 'react';

export const useFlyToTarget = () => {
  const findVisible = useCallback(
    (selector: string, root: ParentNode = document) =>
      [...root.querySelectorAll<HTMLElement>(selector)].filter((element) =>
        element.checkVisibility({ visibilityProperty: true }),
      ),
    [],
  );

  const fly = useCallback(
    async (
      flights: { source: HTMLElement; target: HTMLElement }[],
      { hideSources = false }: { hideSources?: boolean } = {},
    ) => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      const layer = document.querySelector('[class*="css-var-"]') ?? document.body;
      const stagger = Math.min(160, 800 / flights.length);

      await Promise.all(
        flights.map(async ({ source, target }, index) => {
          const from = source.getBoundingClientRect();
          const to = target.getBoundingClientRect();
          const dx = to.left + to.width / 2 - from.left - from.width * 0.175;
          const dy = to.top + to.height / 2 - from.top - from.height / 2;
          const clone = source.cloneNode(true) as HTMLElement;

          if (hideSources) {
            source.style.visibility = 'hidden';
          }

          Object.assign(clone.style, {
            position: 'fixed',
            left: `${from.left}px`,
            top: `${from.top}px`,
            width: `${from.width}px`,
            zIndex: 1100,
            pointerEvents: 'none',
            transformOrigin: '0 50%',
            borderRadius: '8px',
            background: 'var(--ant-color-bg-elevated)',
          });
          layer.append(clone);

          await clone.animate(
            [
              { transform: 'translate(0, 0) scale(1)' },
              { transform: 'translate(0, -6px) scale(1.06)', boxShadow: 'var(--ant-box-shadow-secondary)', offset: 0.15 },
              { transform: `translate(${dx / 2 + 70}px, ${dy / 2}px) scale(1)`, offset: 0.55 },
              { transform: `translate(${dx}px, ${dy}px) scale(0.35)`, opacity: 0.2 },
            ],
            {
              duration: 820,
              delay: 180 + index * stagger,
              easing: 'cubic-bezier(0.45, 0, 0.15, 1)',
              fill: 'both',
            },
          ).finished;

          clone.remove();
          source.style.visibility = '';
          target.animate([{ scale: 1 }, { scale: 1.12 }, { scale: 1 }], {
            duration: 380,
            easing: 'ease-out',
          });
        }),
      );
    },
    [],
  );

  return { findVisible, fly };
};
